'use client'

import { BenefitCardsBlock } from 'cms-types'
import { useEffect, useMemo } from 'react'
import type { Cart, ClubBenefit, Customer } from 'shopify-types'
import { BenefitCards as BenefitCardsUI, type LoadableComponentProps } from 'ui'

import { getCurrentAndNextTier } from '@/cms/helpers'
import { ClubMembershipStatus, useClubMembership } from '@/hooks/useClubMembership'
import { useCart } from '@/store/cartStore'
import { useCustomer } from '@/store/customerStore'
import { useMetaObjects } from '@/store/metaObjectStore'

const BENEFITS_STATE_ORDER: Record<'activeAutomatically' | 'active' | 'inactive' | 'used', number> =
  {
    activeAutomatically: 0,
    active: 1,
    inactive: 2,
    used: 3,
  }

const getOneTimeBenefitState = (
  benefitCode: string,
  customer: Customer | null,
  cartData: Cart | null,
): 'active' | 'inactive' | 'used' => {
  if (customer?.benefitsUsed?.some(b => b.discountCode === benefitCode)) return 'used'
  if (cartData?.discountCodes?.some(c => c.code === benefitCode)) return 'active'
  return 'inactive'
}

export function BenefitCardsClient({ data, loading }: LoadableComponentProps<BenefitCardsBlock>) {
  const customer = useCustomer()
  const membership = useClubMembership()
  const cartData = useCart(s => s.data)
  const applyDiscount = useCart(s => s.applyDiscount)
  const removeDiscount = useCart(s => s.removeDiscount)
  const tierDefinitions = useMetaObjects(s => s.tierDefinitions)
  const getTierDefinitions = useMetaObjects(s => s.getTierDefinitions)
  const loadingMetaObjects = useMetaObjects(s => s.isLoading)

  useEffect(() => {
    getTierDefinitions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const points = customer.customer?.points ?? 0

  const { currentTier } = useMemo(
    () => getCurrentAndNextTier(tierDefinitions, points),
    [tierDefinitions, points],
  )

  const benefits: {
    title: string
    benefitCode: string
    state: 'active' | 'inactive' | 'used' | 'activeAutomatically'
  }[] = useMemo(() => {
    const usedHandles = new Set(
      (customer.customer?.benefitsUsed ?? []).map(benefit => benefit.handle),
    )

    // One-time and individual benefits share the same shape and state logic: shown as
    // used (handle in `benefitsUsed`), otherwise toggleable.
    const mapClaimableBenefit = (benefit: ClubBenefit) => ({
      title: benefit.title,
      benefitCode: benefit.discountCode as string,
      state: usedHandles.has(benefit.handle)
        ? ('used' as const)
        : getOneTimeBenefitState(benefit.discountCode as string, customer.customer, cartData),
    })

    const isRenderableDiscount = (benefit: ClubBenefit) =>
      benefit.type === 'discount' && benefit.discountCode != null

    return [
      // Permanent tier benefits (ClubBenefit metaobjects) are auto-applied. The
      // page-unlock (`access`) type isn't wired up yet, so only discount benefits
      // render as cards.
      ...(currentTier?.permanentBenefits ?? []).filter(isRenderableDiscount).map(benefit => ({
        title: benefit.title,
        benefitCode: benefit.discountCode as string,
        state: 'activeAutomatically' as const,
      })),

      // One-time benefits come from the customer's own `benefitsOnetime` metafield.
      ...(customer.customer?.benefitsOnetime ?? [])
        .filter(isRenderableDiscount)
        .map(mapClaimableBenefit),

      // Individually granted benefits, same handling as one-time benefits.
      ...(customer.customer?.benefitsIndividual ?? [])
        .filter(isRenderableDiscount)
        .map(mapClaimableBenefit),
    ].sort((a, b) => BENEFITS_STATE_ORDER[a.state] - BENEFITS_STATE_ORDER[b.state])
  }, [currentTier, customer.customer, cartData])

  // Members only; empty for non-members and while membership resolves.
  if (membership !== ClubMembershipStatus.Member) return null
  if (loading || loadingMetaObjects) return <BenefitCardsUI loading />

  const onActivate = (benefitCode: string) => applyDiscount([benefitCode])

  const onDeactivate = (benefitCode: string) => removeDiscount([benefitCode])

  return (
    <BenefitCardsUI
      data={{
        benefits: benefits ?? [],
        title: data?.title,
        labels: {
          active: data?.active,
          inactive: data?.inactive,
          used: data?.used,
          activeAutomatically: data?.activeAutomatically,
        },
        onActivate,
        onDeactivate,
      }}
    />
  )
}
